import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, FileText, FileEdit, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two side-by-side tables on the public landing (Feb 2026 — Founder brief):
 *   • Latest Tenders     — hits GET /api/tenders/public-search (already
 *                          runtime-filtered for demo/QA leakage).
 *   • Latest Corrigendum — placeholder empty-state until the Corrigendum
 *                          feature ships (also marked `Soon` in the sidebar).
 *
 * Both tables show: Title · Reference No. · Closing Date · Bid Opening Date,
 * with a per-table "View More" link.
 */
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TableShell = ({ title, icon: Icon, viewAllTo, viewAllLabel, footerNote, headers, children, testid }) => (
  <div
    className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col"
    data-testid={testid}
  >
    {/* Navy header — diagonal-cut trailing edge per reference */}
    <div className="relative flex items-center justify-between text-white pl-4 pr-4 py-2.5">
      <div
        aria-hidden="true"
        className="absolute inset-y-0 left-0 bg-[#0A1F44]"
        style={{ width: '60%', clipPath: 'polygon(0 0, 100% 0, calc(100% - 22px) 100%, 0 100%)' }}
      />
      <div className="relative flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="text-sm font-bold uppercase tracking-wider">{title}</h3>
      </div>
      <Link
        to={viewAllTo}
        className="relative inline-flex items-center gap-1 text-xs font-semibold text-[#0A1F44] hover:text-[#5B21B6] transition-colors"
        data-testid={`${testid}-view-all`}
      >
        {viewAllLabel} <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
          <tr>
            {headers.map((h) => (
              <th key={h.key} className={'px-3 py-2 text-left font-semibold ' + (h.className || '')}>{h.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
    {(footerNote || viewAllTo) && (
      <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 bg-slate-50 text-[11px]">
        <span className="text-slate-500 italic">{footerNote}</span>
        <Link to={viewAllTo} className="text-[#5B21B6] font-semibold hover:underline">View More…</Link>
      </div>
    )}
  </div>
);

const HEADERS = [
  { key: 'title', label: 'Title' },
  { key: 'ref',   label: 'Reference No.', className: 'whitespace-nowrap' },
  { key: 'close', label: 'Closing Date',  className: 'whitespace-nowrap' },
  { key: 'open',  label: 'Bid Opening',   className: 'whitespace-nowrap' },
];

const EmptyRow = ({ colSpan, children }) => (
  <tr>
    <td colSpan={colSpan} className="px-3 py-8 text-center text-sm text-slate-400 italic">{children}</td>
  </tr>
);

export default function PublicLatestTables({ bare = false, stacked = false }) {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchTenders = () => axios.get(`${API}/tenders/public-search?size=5`)
      .then((r) => { if (mounted) { setTenders(r.data?.items || []); setLoading(false); } })
      .catch(() => { if (mounted) setLoading(false); });
    fetchTenders();
    // Poll every 20s so newly-published tenders show up in near-real-time.
    const iv = setInterval(fetchTenders, 20000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  const gridCols = stacked ? 'grid grid-cols-1 gap-5' : 'grid grid-cols-1 xl:grid-cols-2 gap-5';
  const tables = (
    <div className={bare ? gridCols : `max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${gridCols}`}>
      {/* Latest Tenders */}
        <TableShell
          title="Latest Tenders"
          icon={FileText}
          viewAllTo="/tenders/public-search"
          viewAllLabel="View All Tenders"
          footerNote="Latest Tenders updates every 15 mins."
          headers={HEADERS}
          testid="latest-tenders"
        >
          {loading ? (
            <EmptyRow colSpan={HEADERS.length}>
              <Loader2 className="inline w-4 h-4 mr-2 animate-spin" /> Loading latest tenders…
            </EmptyRow>
          ) : tenders.length === 0 ? (
            <EmptyRow colSpan={HEADERS.length}>No live tenders published yet.</EmptyRow>
          ) : (
            tenders.map((t) => (
              <tr
                key={t.id}
                className="border-b border-slate-100 hover:bg-slate-50 last:border-b-0"
                data-testid={`latest-tender-row-${t.id}`}
              >
                <td className="px-3 py-3 max-w-[280px]">
                  <Link
                    to={`/tender/${t.id}`}
                    className="font-semibold text-[#0A1F44] hover:text-[#5B21B6] line-clamp-2"
                    title={t.title}
                  >
                    {t.title || 'Untitled tender'}
                  </Link>
                  {t.buyer_organization && (
                    <div className="text-[11px] text-slate-500 truncate mt-0.5" title={t.buyer_organization}>
                      {t.buyer_organization}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 font-mono text-[11px] text-slate-600 whitespace-nowrap">{t.id}</td>
                <td className="px-3 py-3 text-slate-700 whitespace-nowrap">{fmtDate(t.end_date)}</td>
                <td className="px-3 py-3 text-slate-700 whitespace-nowrap">{fmtDate(t.bid_opening_date || t.end_date)}</td>
              </tr>
            ))
          )}
        </TableShell>

        {/* Latest Corrigendum */}
        <TableShell
          title="Latest Corrigendum"
          icon={FileEdit}
          viewAllTo="/tenders/public-search"
          viewAllLabel="View All Corrigendum"
          footerNote="Latest Corrigendum updates every 15 mins."
          headers={HEADERS}
          testid="latest-corrigendum"
        >
          <EmptyRow colSpan={HEADERS.length}>
            No corrigendum published yet.
            <div className="text-[11px] not-italic mt-1 text-slate-400">
              Corrigendum notices will appear here once buyers publish amendments to live tenders.
            </div>
          </EmptyRow>
        </TableShell>
    </div>
  );

  if (bare) return tables;
  return (
    <section
      className="bg-slate-50 border-y border-slate-200 py-8 lg:py-10"
      aria-label="Latest tenders and corrigendum"
      data-testid="public-latest-tables"
    >
      {tables}
    </section>
  );
}
